import { computed } from 'vue';
import { useMainStore } from '@/stores/mainStore';
import { USER_PERMISSION_ROLE_ENUM } from '@/types/permissions';
import { useUserStore } from '@/stores/userStore';
import { isNil } from 'lodash-es';

export const usePermissions = () => {
  const userStore = useUserStore();
  const mainStore = useMainStore();

  const currentTeamTwrn = computed(() => mainStore.teamTwrn);
  const permissions = computed(() => userStore.permissions);

  const hasRoleForTeam = (teamTwrn: string | null, role: number) => {
    if (!teamTwrn || !permissions.value) return false;

    const permission = permissions.value?.find(
      (perm) => perm.twrn === teamTwrn,
    );

    return permission?.role === role;
  };


  const canManagePaymentsForTeam = (teamTwrn: string | null) => {
    if (!teamTwrn || !permissions.value) return false;

    const permission = permissions.value?.find(
      (perm) => perm.twrn === teamTwrn,
    );

    return permission?.can_manage_payments ?? false;
  };


  const isTeamAdmin = computed(() => {
    return hasRoleForTeam(currentTeamTwrn.value, USER_PERMISSION_ROLE_ENUM.ADMIN);
  });

  const isTeamViewer = computed(() => {
    return hasRoleForTeam(currentTeamTwrn.value, USER_PERMISSION_ROLE_ENUM.VIEWER);
  });

  const isAnyTeamAdmin = computed(() => {
    return permissions.value?.some((perm) => perm.role === USER_PERMISSION_ROLE_ENUM.ADMIN);
  });

  const isTeamAdminForTeam = (teamTwrn: string | null | undefined) => {
    if (isNil(teamTwrn)) return false;
    return hasRoleForTeam(teamTwrn, USER_PERMISSION_ROLE_ENUM.ADMIN);
  };

  const canManagePayments = computed(() => {
    return isTeamAdmin.value && canManagePaymentsForTeam(currentTeamTwrn.value);
  });

  const canSendPayments = computed(() => {
    // If axle profile data is not available (endpoint failed), return false as fallback
    if (!userStore.axleProfile) return false;


    // Normal logic when axle profile data is available
    return canManagePayments.value && userStore.hasActiveWalletProfile;
  });

  return {
    isTeamAdmin,
    isTeamViewer,
    canManagePayments,
    canSendPayments,
    isAnyTeamAdmin,
    isTeamAdminForTeam,
  };
};
